import React, { ReactElement, useEffect, useState } from 'react';
import { DatePicker, FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { useAdminAccountsListMutation } from 'domains/accounts/queries/accounts.query';
import { TAssetEntity } from 'domains/assets/types/assets.types';
import { TCounterpartyEntity } from 'domains/counterparties/types/counterparty.types';
import { ECounterpartyType } from 'domains/payments/enums/payments.enums';
import { EReportingPaymentStatus } from 'domains/reporting/types/reporting.types';
import {
  SelectAccountsCustomersComponent,
} from 'pages/reporting/payments/components/select-accounts-customers/select-accounts-customers.component';
import {
  SButtonsWrapper,
  SContainer,
  SControl,
  SEnhancedSelect,
  SFromInnerWrapper,
  SInputsGroup,
  SLabel,
  SSelectRow,
  STitle,
} from './filters-form.component.style';
import { EFilterKeys, EOperationParticipant } from '../../enums/payments.enum';
import { TParticipantValue } from '../../types/payments.types';

type Props = {
  onSubmit: (values: any) => void;
  assets: TAssetEntity[];
  counterparties: TCounterpartyEntity[];
  form: FormInstance;
  isLoading: boolean;
  onClear: () => void
};

const makeAssetOptions = (assets: TAssetEntity[]): { label: string, value: string }[] =>
  assets.map((el) => ({ value: JSON.stringify(el), label: el.asset_code }));

export const FiltersFormComponent = (
  { onSubmit,assets, counterparties, form, isLoading, onClear }: Props
): ReactElement => {
  const { t } = useTranslation();

  const STATUS_OPTIONS = [
    {
      value: EReportingPaymentStatus.Canceled,
      label: t('reporting.paymentOperations.table.column.status.value.canceled'),
    },
    {
      value: EReportingPaymentStatus.Created,
      label: t('reporting.paymentOperations.table.column.status.value.created'),
    },
    {
      value: EReportingPaymentStatus.Confirmed,
      label: t('reporting.paymentOperations.table.column.status.value.confirmed'),
    },
    {
      value: EReportingPaymentStatus.Refunded,
      label: t('reporting.paymentOperations.table.column.status.value.refunded'),
    },
    {
      value: EReportingPaymentStatus.Scheduled,
      label: t('reporting.paymentOperations.table.column.status.value.scheduled'),
    },
    {
      value: EReportingPaymentStatus.Completed,
      label: t('reporting.paymentOperations.table.column.status.value.completed'),
    },
    {
      value: EReportingPaymentStatus.Failed,
      label: t('reporting.paymentOperations.table.column.status.value.failed'),
    },
  ];

  const [currencyValue, setCurrencyValue] = useState<TAssetEntity | null>(null);
  const [statusValue, setStatusValue] = useState<EReportingPaymentStatus | null>(null);
  const [counterparty1Value, setCounterparty1Value] = useState<string>('');
  const [counterparty2Value, setCounterparty2Value] = useState<string>('');
  const [walletUserExternalId, setWalletUserExternalId] = useState<string>('');
  const [participant1Value, setParticipant1Value] = useState<TParticipantValue>({
    guid: '',
    type: ECounterpartyType.NotSelected,
    walletUserExternalId: '',
  });
  const [participant2Value, setParticipant2Value] = useState<TParticipantValue>({
    guid: '',
    type: ECounterpartyType.NotSelected,
    walletUserExternalId: '',
  });

  const counterpartySelectDisabled = counterparties?.length === 0;

  const [searchString, setSearchString] = useState<string | null>(null);
  const [accountGUID, setAccountGUID] = useState<string>('');
  const [ibanValue, setIbanValue] = useState<string>('');
  const [participantValue, setParticipantValue] = useState<EOperationParticipant>(EOperationParticipant.AllAnd);

  const OP_PARTICIPANTS_OPTIONS = [
    // {
    //   value: EOperationParticipant.AllOr,
    //   label: t(`reporting.paymentOperations.filters.participant.value.allOr`),
    // },
    {
      value: EOperationParticipant.Sender,
      label: t(`reporting.paymentOperations.filters.participant.value.sender`),
    },
    {
      value: EOperationParticipant.Recipient,
      label: t(`reporting.paymentOperations.filters.participant.value.recipient`),
    },
    {
      value: EOperationParticipant.AllAnd,
      label: t(`reporting.paymentOperations.filters.participant.value.allAnd`),
    },
  ];

  const currency = {
    value: currencyValue,
    setValue: setCurrencyValue,
    onChange: (value: TAssetEntity): void => {
      setCurrencyValue(value);
    },
    onClear: (): void => {
      form.setFieldValue(EFilterKeys.AmountMin, null);
      form.setFieldValue(EFilterKeys.AmountMax, null);
    },
    disabled: assets.length === 0,
    options: makeAssetOptions(assets),
  };

  const status = {
    value: statusValue,
    setValue: setStatusValue,
    onChange: (value: EReportingPaymentStatus): void => {
      setStatusValue(value);
    },
    options: STATUS_OPTIONS,
  };

  const operationParticipant = {
    value: participantValue,
    setValue: setParticipantValue,
    onChange: (value: EOperationParticipant): void => {
      setParticipantValue(value);
    },
    options: OP_PARTICIPANTS_OPTIONS,
  };

  const counterparty1 = {
    value: counterparty1Value,
    setValue: setCounterparty1Value,
    onClear: (): void => {
      setParticipant1Value({
        guid: '',
        type: ECounterpartyType.NotSelected,
        walletUserExternalId: '',
      });
    },
    onChange: (value: string): void => {
      setCounterparty1Value(value);
      setParticipant1Value({
        guid: '',
        type: ECounterpartyType.NotSelected,
        walletUserExternalId: '',
      });
      form.setFieldsValue({
        [EFilterKeys.Sender]: [''],
      });
    }
  };

  const counterparty2 = {
    value: counterparty2Value,
    setValue: setCounterparty2Value,
    onClear: (): void => {
      setParticipant2Value({
        guid: '',
        type: ECounterpartyType.NotSelected,
        walletUserExternalId: '',
      });
    },
    onChange: (value: string): void => {
      setCounterparty2Value(value);
      setParticipant2Value({
        guid: '',
        type: ECounterpartyType.NotSelected,
        walletUserExternalId: '',
      });
      form.setFieldsValue({
        [EFilterKeys.Receiver]: [''],
      });
    }
  };

  const treeData = (counterparties || []).map((counterparty) => ({
    label: counterparty.name,
    value: counterparty.guid,
  }));

  const onSearchClear = (): void => {
    setCounterparty1Value('');
    setCounterparty2Value('');
    setParticipant1Value({
      guid: '',
      type: ECounterpartyType.NotSelected,
      walletUserExternalId: '',
    });
    setParticipant2Value({
      guid: '',
      type: ECounterpartyType.NotSelected,
      walletUserExternalId: '',
    });
    setAccountGUID('');
    setIbanValue('');
    setParticipantValue(EOperationParticipant.AllAnd);
    setWalletUserExternalId('');
    form.resetFields();
    onClear();
  }

  const typeSelect = (account_kind: string): ECounterpartyType => {
    switch (account_kind.toLowerCase()) {
      case 'emission': return ECounterpartyType.Bank;
      case 'processing': return ECounterpartyType.Bank;
      case 'customer': return ECounterpartyType.Customer;
      default:
        return ECounterpartyType.NotSelected
    }
  }

  const {
    mutateAsync: mutateAsyncAdminAccountsListMutation,
    isPending: isAdminAccountsListLoading,
  } = useAdminAccountsListMutation();

  const onSearchHandler = async (searchStr: string): Promise<void> => {
    try {
      if (searchStr) {
        const searchStrWithoutSpaces = searchStr.replace(/\s/g, '');
        setSearchString(searchStrWithoutSpaces);

        const data = await mutateAsyncAdminAccountsListMutation(
          {
            params: {
              search_string: searchStrWithoutSpaces,
            },
          },
        );

        if (data?.accounts?.length) {
          const {
            counterparty_guid,
            wallet_user_guid,
            account_kind,
            wallet_account_guid,
            wallet_user_external_id,
            iban,
          } = data?.accounts[0] || {};

          setWalletUserExternalId(wallet_user_external_id);
          setCounterparty1Value(counterparty_guid);
          setParticipant1Value({
            guid: wallet_user_guid,
            type: typeSelect(account_kind),
            walletUserExternalId: wallet_user_external_id,
          });
          setCounterparty2Value(counterparty_guid);
          setParticipant2Value({
            guid: wallet_user_guid,
            type: typeSelect(account_kind),
            walletUserExternalId: wallet_user_external_id,
          });
          setAccountGUID(wallet_account_guid);
          setIbanValue(iban);
        } else {
          form.setFields([
            {
              name: [EFilterKeys.Search],
              errors: [t('reporting.paymentOperations.filters.form.search.error')],
            },
          ]);
        }
      } else {
        onSearchClear();
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    form.setFieldsValue({
      [EFilterKeys.SenderCounterparty]: counterparty1Value,
      [EFilterKeys.Sender]: participant1Value,
      [EFilterKeys.RecipientCounterparty]: counterparty2Value,
      [EFilterKeys.Receiver]: participant2Value,
      sender_acc_kinds: participant1Value,
      receiver_acc_kinds: participant2Value,
    });
  }, [searchString, onSearchHandler]);

  return (
    <SContainer>
      <STitle>{t('reporting.paymentOperations.filters.title')}</STitle>
      <Form
        onFinish={onSubmit}
        form={form}
        layout="vertical"
        initialValues={{
          [EFilterKeys.Participant]: EOperationParticipant.AllAnd,
        }}
      >
        <SFromInnerWrapper>
          <SInputsGroup>
            <Form.Item name={EFilterKeys.GUID} label={t('reporting.paymentOperations.filters.guid.label')}>
              <Input
                style={{ width: '350px' }}
                placeholder={t('reporting.paymentOperations.filters.guid.placeholder')!}
                allowClear
              />
            </Form.Item>
          </SInputsGroup>
          <SInputsGroup>
            <Form.Item name={EFilterKeys.CreatedAt} label={t('reporting.paymentOperations.filters.date.label')}>
              <DatePicker.RangePicker
                allowClear
                placeholder={[
                  t('reporting.paymentOperations.filters.createdAt.from.placeholder'),
                  t('reporting.paymentOperations.filters.createdAt.to.placeholder'),
                ]}
              />
            </Form.Item>
          </SInputsGroup>
          <SInputsGroup
          >
            <SSelectRow>
              <Form.Item
                name={EFilterKeys.Asset}
                label={t('reporting.paymentOperations.filters.amount.currency.label')}
              >
                <SEnhancedSelect
                  style={{ width: '100px' }}
                  value={currency.value}
                  disabled={currency.disabled}
                  placeholder={t('reporting.paymentOperations.filters.amount.currency.placeholder')}
                  onChange={currency.onChange}
                  options={currency.options}
                  allowClear
                  onClear={currency.onClear}
                />
              </Form.Item>
              <Form.Item
                name={EFilterKeys.AmountMin}
                label={t('reporting.paymentOperations.filters.amount.value.min.label')}
              >
                <Input
                  style={{ width: '120px' }}
                  allowClear
                  disabled={!currencyValue}
                  placeholder={t('reporting.paymentOperations.filters.amount.value.min.placeholder')!}
                />
              </Form.Item>
              <Form.Item
                name={EFilterKeys.AmountMax}
                label={t('reporting.paymentOperations.filters.amount.value.max.label')}
              >
                <Input
                  style={{ width: '120px' }}
                  allowClear
                  disabled={!currencyValue}
                  placeholder={t('reporting.paymentOperations.filters.amount.value.max.placeholder')!}
                />
              </Form.Item>
              {/* </Space.Compact> */}
            </SSelectRow>
          </SInputsGroup>
          <SInputsGroup>
            <Form.Item
              name={EFilterKeys.Status}
              label={t('reporting.paymentOperations.filters.status.label')}
            >
              <SEnhancedSelect
                style={{ width: '140px' }}
                value={status.value}
                placeholder={t('reporting.paymentOperations.filters.status.placeholder')}
                onChange={status.onChange}
                options={status.options}
                allowClear
              />
            </Form.Item>
          </SInputsGroup>
          <SInputsGroup>
            <Form.Item
              name={EFilterKeys.Participant}
              label={t('reporting.paymentOperations.filters.participant.label')}
            >
              <SEnhancedSelect
                size="middle"
                value={operationParticipant.value}
                onChange={operationParticipant.onChange}
                options={operationParticipant.options}
              />
            </Form.Item>
          </SInputsGroup>
          <SInputsGroup>
            <Form.Item name={EFilterKeys.Search} label={t('reporting.paymentOperations.filters.search.label')}>
              <Input.Search
                onSearch={onSearchHandler}
                style={{ width: '350px' }}
                placeholder={t('reporting.paymentOperations.filters.search.placeholder')!}
                allowClear
                loading={isAdminAccountsListLoading}
              />
            </Form.Item>
          </SInputsGroup>
          {(participantValue === EOperationParticipant.Sender
            || participantValue === EOperationParticipant.AllAnd) && (
            <SInputsGroup style={{ background: '#f2f2f2', width: '100%' }}>
              <SLabel>
                {t('reporting.paymentOperations.filters.sender.label')}
              </SLabel>
              <SControl>
                <Form.Item name={EFilterKeys.SenderCounterparty}>
                  <SEnhancedSelect
                    style={{
                      width: '300px',
                    }}
                    disabled={counterpartySelectDisabled}
                    value={counterparty1.value}
                    onChange={counterparty1.onChange}
                    onClear={counterparty1.onClear}
                    options={treeData}
                    placeholder={t('reporting.paymentOperations.filters.counterparty.sender.placeholder')}
                    allowClear
                  />
                </Form.Item>
                <Form.Item name={EFilterKeys.Sender}>
                  <SelectAccountsCustomersComponent
                    size="middle"
                    counterparty={counterparty1Value}
                    value={participant1Value.guid}
                    typeValue={participant1Value.type}
                    guid={participant1Value.guid}
                    account_guid={accountGUID}
                    wallet_user_external_id={walletUserExternalId}
                    iban={ibanValue}
                    onChange={setParticipant1Value}
                    showAssetInOption={false}
                    mt={0}
                    placeholderRecipientTransKey="reporting.paymentOperations.filters.select.sender.placeholder"
                  />
                </Form.Item>
              </SControl>
            </SInputsGroup>
          )}
          {(participantValue === EOperationParticipant.Recipient
            || participantValue === EOperationParticipant.AllAnd) && (
            <SInputsGroup style={{ background: '#feffe6', width: '100%' }}>
              <SLabel>
                {t('reporting.paymentOperations.filters.recipient.label')}
              </SLabel>
              <SControl>
                <Form.Item name={EFilterKeys.RecipientCounterparty}>
                  <SEnhancedSelect
                    style={{
                      width: '300px',
                    }}
                    disabled={counterpartySelectDisabled}
                    value={counterparty2.value}
                    onChange={counterparty2.onChange}
                    onClear={counterparty2.onClear}
                    options={treeData}
                    placeholder={t('reporting.paymentOperations.filters.counterparty.receiver.placeholder')}
                    allowClear
                  />
                </Form.Item>
                <Form.Item name={EFilterKeys.Receiver}>
                  <SelectAccountsCustomersComponent
                    size="middle"
                    counterparty={counterparty2Value}
                    value={participant2Value.guid}
                    typeValue={participant2Value.type}
                    guid={participant2Value.guid}
                    account_guid={accountGUID}
                    wallet_user_external_id={walletUserExternalId}
                    iban={ibanValue}
                    onChange={setParticipant2Value}
                    showAssetInOption={false}
                    mt={0}
                  />
                </Form.Item>
              </SControl>
            </SInputsGroup>
          )}
        </SFromInnerWrapper>
        <SButtonsWrapper>
          <Button width={300} type="primary" onClick={onSearchClear}>
            {t('reporting.paymentOperations.filters.button.clear')}
          </Button>
          <Button type="primary" htmlType="submit" disabled={isLoading} loading={isLoading} width={300}>
            {t('reporting.paymentOperations.filters.button.filter')}
          </Button>
        </SButtonsWrapper>
      </Form>
    </SContainer>
  );
};
